import React, { useState, useEffect } from 'react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { FiPlus, FiCheck, FiX, FiGlobe, FiBriefcase, FiSearch } from 'react-icons/fi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService } from '../services/api';

interface Country {
  id: number;
  code: string;
  iso_code: string;
  name: string;
  name_fr: string;
  name_ar: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface Industry {
  id: number;
  name: string;
  name_fr: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

const CountriesIndustries: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'countries' | 'industries'>('countries');
  
  // Countries State
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [editingCountry, setEditingCountry] = useState<number | null>(null);
  const [newCountry, setNewCountry] = useState({ code: '', iso_code: '', name: '', name_fr: '', name_ar: '' });
  const [editCountryData, setEditCountryData] = useState({ code: '', iso_code: '', name: '', name_fr: '', name_ar: '' });
  const [showAddCountry, setShowAddCountry] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  
  // Industries State
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState<number | null>(null);
  const [newIndustry, setNewIndustry] = useState({ name: '', name_fr: '' });
  const [editIndustryData, setEditIndustryData] = useState({ name: '', name_fr: '' });
  const [showAddIndustry, setShowAddIndustry] = useState(false);
  const [industrySearchTerm, setIndustrySearchTerm] = useState('');
  
  // Toast & Confirm Dialog
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'success';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'danger', onConfirm: () => {} });

  useEffect(() => {
    if (activeTab === 'countries') {
      fetchCountries();
    } else {
      fetchIndustries();
    }
  }, [activeTab]);

  // Filter functions
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(countrySearchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(countrySearchTerm.toLowerCase())
  );

  const filteredIndustries = industries.filter(industry =>
    industry.name.toLowerCase().includes(industrySearchTerm.toLowerCase())
  );

  // Countries Functions
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await apiService.getCountries();
      if (response.success && response.data) {
        setCountries(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load countries', type: 'error' });
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleAddCountry = async () => {
    if (!newCountry.code || !newCountry.iso_code || !newCountry.name) {
      setToast({ message: 'Please fill required fields (Code, ISO Code, and English Name)', type: 'error' });
      return;
    }

    try {
      const response = await apiService.createCountry(newCountry);
      if (response.success) {
        setCountries([...countries, response.data]);
        setNewCountry({ code: '', iso_code: '', name: '', name_fr: '', name_ar: '' });
        setShowAddCountry(false);
        setToast({ message: 'Country added successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to add country', type: 'error' });
    }
  };

  const handleUpdateCountry = async (id: number) => {
    if (!editCountryData.code || !editCountryData.iso_code || !editCountryData.name) {
      setToast({ message: 'Please fill required fields (Code, ISO Code, and English Name)', type: 'error' });
      return;
    }

    try {
      const response = await apiService.updateCountry(id, editCountryData);
      if (response.success) {
        setCountries(countries.map(c => c.id === id ? response.data : c));
        setEditingCountry(null);
        setToast({ message: 'Country updated successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to update country', type: 'error' });
    }
  };

  const handleDeleteCountry = async (id: number) => {
    try {
      const response = await apiService.deleteCountry(id);
      if (response.success) {
        setCountries(countries.filter(c => c.id !== id));
        setToast({ message: 'Country deleted successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to delete country', type: 'error' });
    }
  };

  const handleToggleCountry = async (id: number) => {
    try {
      const response = await apiService.toggleCountryStatus(id);
      if (response.success) {
        setCountries(countries.map(c => 
          c.id === id ? { ...c, enabled: response.data.enabled } : c
        ));
        setToast({ 
          message: `Country ${response.data.enabled ? 'enabled' : 'disabled'} successfully`, 
          type: 'success' 
        });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to toggle country status', type: 'error' });
    }
  };

  const confirmDeleteCountry = (id: number, name: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Country',
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: () => {
        handleDeleteCountry(id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  // Industries Functions
  const fetchIndustries = async () => {
    setLoadingIndustries(true);
    try {
      const response = await apiService.getIndustries();
      if (response.success && response.data) {
        setIndustries(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load industries', type: 'error' });
    } finally {
      setLoadingIndustries(false);
    }
  };

  const handleAddIndustry = async () => {
    if (!newIndustry.name) {
      setToast({ message: 'Please fill the name field', type: 'error' });
      return;
    }

    try {
      const response = await apiService.createIndustry(newIndustry);
      if (response.success) {
        setIndustries([...industries, response.data]);
        setNewIndustry({ name: '', name_fr: '' });
        setShowAddIndustry(false);
        setToast({ message: 'Industry added successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to add industry', type: 'error' });
    }
  };

  const handleUpdateIndustry = async (id: number) => {
    if (!editIndustryData.name) {
      setToast({ message: 'Please fill the name field', type: 'error' });
      return;
    }

    try {
      const response = await apiService.updateIndustry(id, editIndustryData);
      if (response.success) {
        setIndustries(industries.map(i => i.id === id ? response.data : i));
        setEditingIndustry(null);
        setToast({ message: 'Industry updated successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to update industry', type: 'error' });
    }
  };

  const handleDeleteIndustry = async (id: number) => {
    try {
      const response = await apiService.deleteIndustry(id);
      if (response.success) {
        setIndustries(industries.filter(i => i.id !== id));
        setToast({ message: 'Industry deleted successfully', type: 'success' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to delete industry', type: 'error' });
    }
  };

  const handleToggleIndustry = async (id: number) => {
    try {
      const response = await apiService.toggleIndustryStatus(id);
      if (response.success) {
        setIndustries(industries.map(i => 
          i.id === id ? { ...i, enabled: response.data.enabled } : i
        ));
        setToast({ 
          message: `Industry ${response.data.enabled ? 'enabled' : 'disabled'} successfully`, 
          type: 'success' 
        });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to toggle industry status', type: 'error' });
    }
  };

  const confirmDeleteIndustry = (id: number, name: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Industry',
      message: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: () => {
        handleDeleteIndustry(id);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      },
    });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">⚙️ Settings</h1>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('countries')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'countries'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FiGlobe className="h-5 w-5" />
                <span>Countries ({countries.length})</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('industries')}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'industries'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FiBriefcase className="h-5 w-5" />
                <span>Industries ({industries.length})</span>
              </div>
            </button>
          </div>

          {/* Countries Tab Content */}
          {activeTab === 'countries' && (
            <div className="p-6">
              {/* Search and Add Button */}
              <div className="mb-4 flex items-center gap-4">
                <div className="relative flex-1">
                  <FiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by country name or code..."
                    value={countrySearchTerm}
                    onChange={(e) => setCountrySearchTerm(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <button
                  onClick={() => setShowAddCountry(!showAddCountry)}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  <FiPlus className="h-5 w-5" />
                  Add Country
                </button>
              </div>

              {/* Add Country Form */}
              {showAddCountry && (
                <div className="mb-4 rounded-lg bg-gray-50 p-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <input
                        type="text"
                        placeholder="Phone Code (e.g., +216) *"
                        value={newCountry.code}
                        onChange={(e) => setNewCountry({ ...newCountry, code: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="ISO Code (e.g., TN) *"
                        value={newCountry.iso_code}
                        onChange={(e) => setNewCountry({ ...newCountry, iso_code: e.target.value.toUpperCase() })}
                        maxLength={2}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Country Name (English) *"
                        value={newCountry.name}
                        onChange={(e) => setNewCountry({ ...newCountry, name: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        placeholder="Country Name (French)"
                        value={newCountry.name_fr}
                        onChange={(e) => setNewCountry({ ...newCountry, name_fr: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Country Name (Arabic)"
                        value={newCountry.name_ar}
                        onChange={(e) => setNewCountry({ ...newCountry, name_ar: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                        dir="rtl"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddCountry}
                        className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700"
                      >
                        <FiCheck className="mx-auto h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setShowAddCountry(false);
                          setNewCountry({ code: '', iso_code: '', name: '', name_fr: '', name_ar: '' });
                        }}
                        className="flex-1 rounded-lg bg-gray-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-600"
                      >
                        <FiX className="mx-auto h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Countries Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">Phone Code</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">ISO</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">Name (EN)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">Name (FR)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingCountries ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-600">
                          Loading countries...
                        </td>
                      </tr>
                    ) : filteredCountries.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-600">
                          {countries.length === 0 
                            ? 'No countries found. Add one to get started.'
                            : 'No countries match your search.'}
                        </td>
                      </tr>
                    ) : (
                      filteredCountries.map((country) => (
                        <tr key={country.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{country.id}</td>
                          <td className="px-4 py-3">
                            {editingCountry === country.id ? (
                              <input
                                type="text"
                                value={editCountryData.code}
                                onChange={(e) => setEditCountryData({ ...editCountryData, code: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                              />
                            ) : (
                              <span className="text-sm font-medium text-blue-600">{country.code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {editingCountry === country.id ? (
                              <input
                                type="text"
                                value={editCountryData.iso_code}
                                onChange={(e) => setEditCountryData({ ...editCountryData, iso_code: e.target.value.toUpperCase() })}
                                maxLength={2}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="ISO code"
                              />
                            ) : (
                              <span className="text-sm font-bold text-green-600">{country.iso_code}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {editingCountry === country.id ? (
                              <input
                                type="text"
                                value={editCountryData.name}
                                onChange={(e) => setEditCountryData({ ...editCountryData, name: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="English name"
                              />
                            ) : (
                              <span className="text-sm text-gray-900">{country.name}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {editingCountry === country.id ? (
                              <input
                                type="text"
                                value={editCountryData.name_fr}
                                onChange={(e) => setEditCountryData({ ...editCountryData, name_fr: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="French name"
                              />
                            ) : (
                              <span className="text-sm text-gray-600">{country.name_fr || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {editingCountry === country.id ? (
                                <>
                                  <button
                                    onClick={() => handleUpdateCountry(country.id)}
                                    className="rounded-lg bg-green-600 p-2 text-white transition hover:bg-green-700"
                                  >
                                    <FiCheck className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingCountry(null)}
                                    className="rounded-lg bg-gray-500 p-2 text-white transition hover:bg-gray-600"
                                  >
                                    <FiX className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  {/* Toggle Switch */}
                                  <button
                                    onClick={() => handleToggleCountry(country.id)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      country.enabled ? 'bg-green-500' : 'bg-gray-300'
                                    }`}
                                    title={country.enabled ? 'Enabled - Click to disable' : 'Disabled - Click to enable'}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        country.enabled ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingCountry(country.id);
                                      setEditCountryData({ 
                                        code: country.code,
                                        iso_code: country.iso_code,
                                        name: country.name,
                                        name_fr: country.name_fr || '',
                                        name_ar: country.name_ar || ''
                                      });
                                    }}
                                    className="rounded-lg bg-blue-600 p-2 text-white transition hover:bg-blue-700"
                                  >
                                    <MdEdit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteCountry(country.id, country.name)}
                                    className="rounded-lg bg-red-600 p-2 text-white transition hover:bg-red-700"
                                  >
                                    <MdDelete className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Industries Tab Content */}
          {activeTab === 'industries' && (
            <div className="p-6">
              {/* Search and Add Button */}
              <div className="mb-4 flex items-center gap-4">
                <div className="relative flex-1">
                  <FiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by industry name..."
                    value={industrySearchTerm}
                    onChange={(e) => setIndustrySearchTerm(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <button
                  onClick={() => setShowAddIndustry(!showAddIndustry)}
                  className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  <FiPlus className="h-5 w-5" />
                  Add Industry
                </button>
              </div>

              {/* Add Industry Form */}
              {showAddIndustry && (
                <div className="mb-4 rounded-lg bg-gray-50 p-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <input
                        type="text"
                        placeholder="Industry Name (English) *"
                        value={newIndustry.name}
                        onChange={(e) => setNewIndustry({ ...newIndustry, name: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Industry Name (French)"
                        value={newIndustry.name_fr}
                        onChange={(e) => setNewIndustry({ ...newIndustry, name_fr: e.target.value })}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddIndustry}
                        className="flex-1 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-green-700"
                      >
                        <FiCheck className="mx-auto h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setShowAddIndustry(false);
                          setNewIndustry({ name: '', name_fr: '' });
                        }}
                        className="flex-1 rounded-lg bg-gray-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-600"
                      >
                        <FiX className="mx-auto h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Industries Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">Name (EN)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-600">Name (FR)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingIndustries ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-600">
                          Loading industries...
                        </td>
                      </tr>
                    ) : filteredIndustries.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-600">
                          {industries.length === 0 
                            ? 'No industries found. Add one to get started.'
                            : 'No industries match your search.'}
                        </td>
                      </tr>
                    ) : (
                      filteredIndustries.map((industry) => (
                        <tr key={industry.id} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{industry.id}</td>
                          <td className="px-4 py-3">
                            {editingIndustry === industry.id ? (
                              <input
                                type="text"
                                value={editIndustryData.name}
                                onChange={(e) => setEditIndustryData({ ...editIndustryData, name: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="English name"
                              />
                            ) : (
                              <span className="text-sm text-gray-900">{industry.name}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {editingIndustry === industry.id ? (
                              <input
                                type="text"
                                value={editIndustryData.name_fr}
                                onChange={(e) => setEditIndustryData({ ...editIndustryData, name_fr: e.target.value })}
                                className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
                                placeholder="French name"
                              />
                            ) : (
                              <span className="text-sm text-gray-600">{industry.name_fr || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              {editingIndustry === industry.id ? (
                                <>
                                  <button
                                    onClick={() => handleUpdateIndustry(industry.id)}
                                    className="rounded-lg bg-green-600 p-2 text-white transition hover:bg-green-700"
                                  >
                                    <FiCheck className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingIndustry(null)}
                                    className="rounded-lg bg-gray-500 p-2 text-white transition hover:bg-gray-600"
                                  >
                                    <FiX className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  {/* Toggle Switch */}
                                  <button
                                    onClick={() => handleToggleIndustry(industry.id)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      industry.enabled ? 'bg-green-500' : 'bg-gray-300'
                                    }`}
                                    title={industry.enabled ? 'Enabled - Click to disable' : 'Disabled - Click to enable'}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        industry.enabled ? 'translate-x-6' : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingIndustry(industry.id);
                                      setEditIndustryData({ name: industry.name, name_fr: industry.name_fr || '' });
                                    }}
                                    className="rounded-lg bg-blue-600 p-2 text-white transition hover:bg-blue-700"
                                  >
                                    <MdEdit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteIndustry(industry.id, industry.name)}
                                    className="rounded-lg bg-red-600 p-2 text-white transition hover:bg-red-700"
                                  >
                                    <MdDelete className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </Layout>
  );
};

export default CountriesIndustries;
